'use client';

import React, { createContext, useContext, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';
import { useUser } from '@auth0/nextjs-auth0/client';
import { ICompany, ICompanyConfig, ProviderProps } from '../types/types';
import { useToast } from './toast-provider';
import { useUserContext } from './UserContext';
import axios, { AxiosResponse } from 'axios';
import { UserRole } from '@/constants/enums';
import { getAppRolesFromUser } from '@/utils/app-roles.util';
import { isPublicPageRoute } from '@/utils/isPublicPageRoute.util';
import { getCompanySlugFromPath } from '@/utils/return-to.util';
export const dynamic = 'force-dynamic';
const COMPANY_QUERY_KEY = 'company';

// Create a context to manage Company-related data and functions
const CompanyContext = createContext({
  error: '',
  company: {},
  companyConfig: {},
  getCompanyDetails: async (data, setIsLoading) => {},
  isCompanyAccessDisabled: false,
  isCompanyReadonly: false,
  /** True once the company + config query has succeeded — flags read as "off" before that. */
  isCompanyLoaded: false
});
// Create a hook to access the CompanyContext
const useCompanyContext = () => useContext(CompanyContext);

// Create a component
const CompanyProvider = ({
  children
}) => {
  const {
    showErrorToast
  } = useToast();
  const router = useRouter();
  const pathname = usePathname();
  const {
    user: auth0User,
    isLoading: isAuth0Loading
  } = useUser();
  const {
    user: contextUser,
    isUserLoading
  } = useUserContext();
  const companyId = auth0User?.companyId ?? contextUser?.companyId;
  const queryKey = [COMPANY_QUERY_KEY, companyId];
  const {
    data,
    error: queryError,
    refetch,
    isSuccess: isCompanyQuerySuccess
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await axios.get('/api/company', {
        params: {
          requestType: 'orgId'
        }
      });
      return res.data;
    },
    enabled: Boolean(companyId) && !isAuth0Loading && !isUserLoading,
    throwOnError: false
  });
  const companyData = data?.data;
  const company = companyData ?? {};
  const companyConfig = companyData?.companyConfig ?? {};
  const error = typeof queryError === 'string' ? queryError : queryError?.message ?? '';
  const getCompanyDetails = useCallback(async (params, setIsLoading) => {
    if (!companyId) return;
    if (company && Object.keys(company).length > 0) return;
    setIsLoading(true);
    try {
      await refetch();
    } finally {
      setIsLoading(false);
    }
  }, [company, companyId, refetch]);
  const roles = useMemo(() => {
    return getAppRolesFromUser(auth0User);
  }, [auth0User]);
  const isCompanyAccessDisabled = useMemo(() => {
    if (!auth0User) return false;
    if (!companyId) return false;
    if (!isCompanyQuerySuccess) return false;
    return companyConfig.isAccessDisabled === true;
  }, [auth0User, companyId, isCompanyQuerySuccess, pathname, companyConfig.isAccessDisabled]);
  const isCompanyReadonly = useMemo(() => {
    if (!auth0User) return false;
    if (!companyId) return false;
    if (!isCompanyQuerySuccess) return false;
    return companyConfig.isCompanyReadonly === true;
  }, [auth0User, companyId, isCompanyQuerySuccess, pathname, companyConfig.isCompanyReadonly]);
  useEffect(() => {
    // Off the active company's slug (another company's link, or `/`) → its home. Exact segment match:
    // a substring check let `/acme-corp/...` through while on `acme`. No automatic company switch.
    if (company?.companySlug && getCompanySlugFromPath(pathname) !== company.companySlug && !isPublicPageRoute(pathname) && roles.length > 0 && !roles.includes(UserRole.SuperAdmin)) {
      router.push(`/${company.companySlug}`);
    }
  }, [company?.companySlug, pathname, roles, router]);
  useEffect(() => {
    if (queryError) {
      showErrorToast(typeof queryError === 'string' ? queryError : queryError?.message ?? '');
    }
  }, [queryError, showErrorToast]);
  return <CompanyContext.Provider value={{
    error,
    company,
    companyConfig,
    getCompanyDetails,
    isCompanyAccessDisabled,
    isCompanyReadonly,
    isCompanyLoaded: isCompanyQuerySuccess
  }}>
            {children}
        </CompanyContext.Provider>;
};

// Export the context, provider component, and custom hook
export { CompanyContext, CompanyProvider, useCompanyContext };